/**
 * Trainer Live Sessions — list of upcoming + happening-now sessions.
 * Embedded in Community.jsx "Live" tab. Joining opens /live/:id which renders Jitsi iframe.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Video, Calendar, Users, IndianRupee, Plus } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { toast } from "sonner";

export default function LiveSessions() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [sessions, setSessions] = useState([]);
  const [filter, setFilter] = useState("upcoming");
  const [creating, setCreating] = useState(false);
  const [isPro, setIsPro] = useState(false);

  const load = () => apiClient.get(`/live/sessions?filter=${filter}`).then((r) => setSessions(r.data?.sessions || []));
  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    if (!user) return;
    apiClient.get("/professionals/me").then((r) => setIsPro(r.data?.status === "verified")).catch(() => setIsPro(false));
  }, [user]);

  return (
    <div data-testid="live-sessions-tab" className="px-1">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0 12px" }}>
        <div style={{ display: "flex", gap: 6 }}>
          {["upcoming", "live"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`live-filter-${f}`}
              style={{ padding: "6px 12px", background: filter === f ? "#0C0C0B" : "transparent", color: filter === f ? "white" : "var(--text-secondary, #4A4845)", border: "1px solid", borderColor: filter === f ? "#0C0C0B" : "var(--border, rgba(12,12,11,0.16))", borderRadius: 100, fontSize: 12, fontWeight: 700, textTransform: "capitalize" }}>{f}</button>
          ))}
        </div>
        {isPro && (
          <button onClick={() => setCreating(true)} data-testid="host-live-btn"
            style={{ padding: "8px 14px", background: "#A52121", color: "white", border: "none", borderRadius: 100, fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Plus size={12} /> Host
          </button>
        )}
      </div>
      {sessions.length === 0 ? (
        <div style={{ textAlign: "center", padding: 30 }}>
          <Video size={32} style={{ margin: "0 auto", color: "var(--text-tertiary, #8A8884)" }} />
          <div style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 800, marginTop: 8 }}>No {filter === "live" ? "live" : "upcoming"} sessions</div>
          <p style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)" }}>Check back soon — verified trainers host sessions weekly.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {sessions.map((s) => (
            <button key={s.live_session_id} onClick={() => navigate(`/live/${s.live_session_id}`)} data-testid={`live-${s.live_session_id}`}
              style={{ textAlign: "left", padding: 14, background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderLeft: s.status === "live" ? "3px solid #A52121" : "3px solid transparent", borderRadius: 12 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                {s.trainer_avatar ? <img src={s.trainer_avatar} alt="" width={40} height={40} style={{ borderRadius: "50%", flexShrink: 0 }} /> : (
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--bg-inset, #D8D6D1)", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 14, flexShrink: 0 }}>{(s.trainer_name || "?").slice(0, 1).toUpperCase()}</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 800, lineHeight: 1.05 }}>{s.title}</div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)" }}>by {s.trainer_name}</div>
                  <div style={{ display: "flex", gap: 12, fontSize: 11, color: "var(--text-secondary, #4A4845)", marginTop: 6 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Calendar size={10} /> {new Date(s.scheduled_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })}</span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Users size={10} /> {s.current_participants}/{s.max_participants}</span>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  {s.status === "live" && <span style={{ display: "inline-block", padding: "3px 8px", background: "#A52121", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 100, textTransform: "uppercase", letterSpacing: "0.06em" }}>Live</span>}
                  {s.type === "paid" ? (
                    <div style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 900, marginTop: 4 }}>₹{s.price_inr}</div>
                  ) : (
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#16A34A", marginTop: 4 }}>FREE</div>
                  )}
                </div>
              </div>
              {s.description && <p style={{ fontSize: 12, color: "var(--text-secondary, #4A4845)", marginTop: 8 }}>{s.description}</p>}
            </button>
          ))}
        </div>
      )}
      {creating && <CreateLiveModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function CreateLiveModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", description: "", scheduled_at: "", duration_min: 45, type: "free", price_inr: 0, max_participants: 30 });
  const [busy, setBusy] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.title || !form.scheduled_at) return toast.error("Title + time required");
    if (form.type === "paid" && form.price_inr < 49) return toast.error("Paid sessions must be ≥ ₹49");
    setBusy(true);
    try {
      await apiClient.post("/live/sessions", { ...form, scheduled_at: new Date(form.scheduled_at).toISOString() });
      toast.success("Live session scheduled");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] grid place-items-end bg-black/50" onClick={onClose} data-testid="create-live-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full bg-white rounded-t-[20px] p-6 max-h-[85vh] overflow-y-auto">
        <h3 style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800 }}>Host a Live Session</h3>
        <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
          <input value={form.title} onChange={(e) => update("title", e.target.value.slice(0, 80))} placeholder="Title" data-testid="live-title"
            style={{ height: 44, padding: "0 14px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontSize: 14, outline: "none" }} />
          <textarea value={form.description || ""} onChange={(e) => update("description", e.target.value.slice(0, 300))} placeholder="Description (optional)" rows={2}
            style={{ padding: 14, background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontSize: 14, resize: "none", outline: "none" }} />
          <input type="datetime-local" value={form.scheduled_at} onChange={(e) => update("scheduled_at", e.target.value)} data-testid="live-when"
            style={{ height: 44, padding: "0 14px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontSize: 14, outline: "none" }} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <select value={form.duration_min} onChange={(e) => update("duration_min", parseInt(e.target.value, 10))}
              style={{ height: 44, padding: "0 14px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontSize: 14 }}>
              {[15, 30, 45, 60, 75, 90].map((n) => <option key={n} value={n}>{n} min</option>)}
            </select>
            <select value={form.max_participants} onChange={(e) => update("max_participants", parseInt(e.target.value, 10))}
              style={{ height: 44, padding: "0 14px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontSize: 14 }}>
              {[10, 20, 30, 50, 100, 200].map((n) => <option key={n} value={n}>{n} max</option>)}
            </select>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <select value={form.type} onChange={(e) => update("type", e.target.value)} data-testid="live-type"
              style={{ height: 44, padding: "0 14px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontSize: 14 }}>
              <option value="free">Free</option><option value="paid">Paid</option>
            </select>
            {form.type === "paid" && (
              <div style={{ position: "relative" }}>
                <IndianRupee size={14} style={{ position: "absolute", left: 12, top: 16, color: "var(--text-tertiary, #8A8884)" }} />
                <input type="number" min={49} max={5000} value={form.price_inr || ""} onChange={(e) => update("price_inr", parseInt(e.target.value || "0", 10))} placeholder="Price (≥49)"
                  style={{ width: "100%", height: 44, padding: "0 14px 0 32px", background: "var(--bg-inset, #D8D6D1)", border: "none", borderRadius: 12, fontSize: 14, outline: "none" }} />
              </div>
            )}
          </div>
          {form.type === "paid" && <p style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)" }}>FitFrik takes 10%, you keep 90%.</p>}
          <button onClick={submit} disabled={busy} data-testid="live-submit"
            style={{ height: 48, background: "#A52121", color: "white", border: "none", borderRadius: 100, fontSize: 14, fontWeight: 800 }}>
            {busy ? "Scheduling…" : "Schedule live session"}
          </button>
        </div>
      </div>
    </div>
  );
}
